<template>
  <div>
    <link rel="stylesheet" href="https://use.fontawesome.com/releases/v5.8.1/css/all.css" integrity="sha384-50oBUHEmvpQ+1lW4y57PTFmhCaXp0ML5d60M1M7uH2+nqUivzIebhndOJK28anvf" crossorigin="anonymous">
    <div class="card__wrapper">
      <div class="card">
        <h1 class="card__title">{{ $t('messages.create') }}</h1>
        <div class="card__description">
          <b-form @submit.prevent="create">
            <b-form-group id="typeGroup"
                          v-bind:label="$t('messages.type')+':'"
                          label-for="type"
                          :invalid-feedback="$t('errors.type')">
              <b-form-select id="type" name="type"
                             v-model="form.type" :disabled="types.length === 0"
                             @change="$v.form.type.$touch"
                             :state="$v.form.type.$dirty ? !$v.form.type.$error : null">
                <option v-for="type in types" :key="type.id" :value="type.id">{{ $t('codes.' + type.name.toLowerCase()) }}</option>
              </b-form-select>
            </b-form-group>
            <b-form-group id="titleGroup"
                          v-bind:label="$t('messages.title')+':'"
                          label-for="name">
              <vue-bootstrap-typeahead :data="searchResults" v-model="search" :serializer="s => s.name" @hit="select" />
            </b-form-group>
            <b-form-group id="imageGroup"
                          v-bind:label="$t('messages.image')+':'"
                          label-for="image">
              <b-img id="image" name="image" :src="form.image" fluid :alt="form.title" v-if="selectedSearch"></b-img>
              <p v-if="!selectedSearch" class="font-italic">{{ $t('messages.image_none') }}</p>
            </b-form-group>
            <b-form-group id="descriptionGroup"
                          v-bind:label="$t('messages.description')+':'"
                          label-for="description">
              <ckeditor id="description" name="description" :editor="editor.instance" v-model="editor.data"
                        :config="editor.config" :key="editor.update" :disabled="!selectedSearch"></ckeditor>
            </b-form-group>
            <b-form-group id="urlGroup"
                          v-bind:label="$t('messages.url')+':'"
                          label-for="url">
              <b-form-input id="url" name="url" type="text" :disabled="true"
                            v-model.trim="form.url"></b-form-input>
            </b-form-group>
            <b-form-group id="expectedDateGroup"
                          v-bind:label="$t('messages.expected_date')+':'"
                          label-for="expectedDate">
              <date-picker id="expectedDate" name="expectedDate" v-model="form.expectedDate" :config="expectedDateOptions"
                           @input="$v.form.expectedDate.$touch" :disabled="!selectedSearch"></date-picker>
            </b-form-group>
            <b-form-group id="deadlineGroup"
                          v-bind:label="$t('messages.deadline')+':'"
                          label-for="deadline">
              <date-picker id="deadline" name="deadline" v-model="form.deadlineDate" :config="deadlineDateOptions"
                           @input="$v.form.deadlineDate.$touch" :disabled="!selectedSearch"></date-picker>
            </b-form-group>
            <b-form-group id="privateGroup">
              <b-form-checkbox id="private" name="private"
                               v-model="form.private"
                               class="checkboxNoMargin">&nbsp;</b-form-checkbox>
              <span class="checkboxLineHeight pointer" @click="click('private')">{{ $t('messages.private') }}</span>
            </b-form-group>
            <b-form-group id="shippingGroup">
              <b-form-checkbox id="shipping" name="shipping"
                               v-model="form.shipping"
                               class="checkboxNoMargin">&nbsp;</b-form-checkbox>
              <span class="checkboxLineHeight pointer" @click="click('shipping')">{{ $t('messages.shipping_want') }}</span>
            </b-form-group>
            <b-form-group id="copyPaymentInfoGroup">
              <b-form-checkbox id="copyPaymentInfo" name="copyPaymentInfo"
                               v-model="form.copyPaymentInfo"
                               class="checkboxNoMargin">&nbsp;</b-form-checkbox>
              <span class="checkboxLineHeight pointer" @click="click('copyPaymentInfo')">{{ $t('messages.copy_payment_info') }}</span>
            </b-form-group>
            <b-form-group id="copyPickupPointGroup">
              <b-form-checkbox id="copyPickupPoint" name="copyPickupPoint"
                               v-model="form.copyPickupPoint"
                               class="checkboxNoMargin">&nbsp;</b-form-checkbox>
              <span class="checkboxLineHeight pointer" @click="click('copyPickupPoint')">{{ $t('messages.copy_pickup_point') }}</span>
            </b-form-group>
            <b-form-group id="lightlyGroup">
              <b-form-checkbox id="lightly" name="lightly"
                               v-model="form.lightly"
                               @input="$v.form.lightly.$touch"
                               :state="$v.form.lightly.$dirty ? !$v.form.lightly.$error : null"
                               class="checkboxNoMargin">&nbsp;</b-form-checkbox>
              <span class="checkboxLineHeight pointer" @click="click('lightly')">{{ $t('messages.lightly') }}</span>
            </b-form-group>
            <b-form-group id="disclaimerGroup">
              <b-form-checkbox id="disclaimer" name="disclaimer"
                               v-model="form.disclaimer"
                               @input="$v.form.disclaimer.$touch"
                               :state="$v.form.disclaimer.$dirty ? !$v.form.disclaimer.$error : null"
                               class="checkboxNoMargin">&nbsp;</b-form-checkbox>
              <span class="checkboxLineHeight pointer" @click="click('disclaimer')">{{ $t('messages.disclaimer_pre') }} <b-button variant="link" class="buttonNoPadding" v-b-modal.disclaimerModal>{{ $t('messages.disclaimer') }}</b-button> {{ $t('messages.disclaimer_post') }}</span>
            </b-form-group>
            <notification-button :message="$t('messages.create')" :notification="notifications.create" field="create"
                                 :disabled="$v.form.$invalid"></notification-button>
          </b-form>
        </div>
      </div>
    </div>

    <b-modal id="disclaimerModal" :title="$t('messages.disclaimer_title')" hide-footer>
      <p v-html="getDisclaimer()"></p>
    </b-modal>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, sameAs } from 'vuelidate/lib/validators'
import NotificationButton from '../../components/NotificationButton'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'

import DatePicker from 'vue-bootstrap-datetimepicker'

import { defaultMixin } from '../../mixin/default'
import { notificationMixin } from '../../mixin/notification'

import { projectService } from '../../services/project.service'
import { kickstarterService } from '../../services/kickstarter.service'
import { gamefoundService } from '../../services/gamefound.service'

import _ from 'lodash'

import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css'

export default {
  name: 'createproject',
  metaInfo: function () {
    return {
      title: this.$i18n.t('messages.create') + ' - CrowdFinder',
      meta: [
        {
          'property': 'og:title',
          'content': this.$i18n.t('messages.create'),
          'vmid': 'og:title'
        }
      ]
    }
  },
  components: { NotificationButton, DatePicker },
  data: function () {
    return {
      form: {
        type: null,
        title: null,
        image: null,
        imageBig: null,
        description: null,
        url: null,
        expectedDate: null,
        deadlineDate: null,
        private: false,
        shipping: false,
        copyPaymentInfo: false,
        copyPickupPoint: false,
        lightly: false,
        disclaimer: false
      },
      types: [],
      search: '',
      selectedSearch: null,
      searchResults: [],
      editor: {
        instance: ClassicEditor,
        data: '',
        config: {
          toolbar: [ 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', 'insertTable', 'undo', 'redo' ],
          language: this.$i18n.locale
        },
        update: 0
      },
      expectedDateOptions: {
        format: 'MM-YYYY',
        locale: this.$i18n.locale
      },
      deadlineDateOptions: {
        format: 'YYYY-MM-DD HH:mm:ss',
        locale: this.$i18n.locale
      },
      notifications: {
        create: null
      }
    }
  },
  mixins: [ validationMixin, defaultMixin, notificationMixin ],
  validations: {
    form: {
      type: {
        required
      },
      description: {
        required
      },
      expectedDate: {
        required
      },
      deadlineDate: {
        required
      },
      lightly: {
        sameAs: sameAs(() => true)
      },
      disclaimer: {
        sameAs: sameAs(() => true)
      }
    }
  },
  methods: {
    select: function (event) {
      let loader = this.$loading.show()
      this.selectedSearch = event
      if (this.selectedSearch) {
        this.form.title = this.selectedSearch.name
        this.form.description = this.selectedSearch.description
        this.form.image = this.selectedSearch.photoSmall
        this.form.imageBig = this.selectedSearch.photoBig
        this.form.url = this.selectedSearch.url
        this.form.deadlineDate = this.selectedSearch.deadline

        this.editor.data = this.selectedSearch.description

        if (this.form.type === 1) {
          kickstarterService.getExpectedDate(this.form.url)
            .then((expectedDate) => {
              this.form.expectedDate = expectedDate
              loader.hide()
            }, () => {
              loader.hide()
            })
        } else if (this.form.type === 3) {
          gamefoundService.getExpectedDate(this.form.url)
            .then((expectedDate) => {
              this.form.expectedDate = expectedDate
              gamefoundService.getImage(this.form.url)
                .then((image) => {
                  this.form.image = image
                  this.form.imageBig = image
                  loader.hide()
                }, () => {
                  loader.hide()
                })
            }, () => {
              loader.hide()
            })
        }

        this.$v.form.description.$touch()
        this.$v.form.deadlineDate.$touch()
      }
    },
    getDisclaimer: function () {
      let disclaimerUrl = 'i18n/create/' + this.$i18n.locale + '.html'
      return require('@/' + disclaimerUrl)
    },
    click: function (id) {
      document.getElementById(id).click()
    },
    create: function () {
      if (!this.$v.form.$invalid) {
        let project = this.$v.form.$model
        let store = this.$store
        let router = this.$router

        projectService.create(project)
          .then((response) => {
            store.commit('userTotalProjects', store.state.userTotalProjects + 1)
            router.push('/project/' + this.slugText(response.id + '/' + response.name))
            setTimeout(() => store.commit('success', 'success.create'))
          }, () => {
            store.commit('error', 'errors.create')
            this.sendNotification('create', 'error')
          })
      }
    }
  },
  created: function () {
    let store = this.$store

    projectService.fetchTypes()
      .then((types) => {
        this.types = types
        this.form.type = types[0].id
        this.$v.form.type.$touch()
      }, () => {
        store.commit('error', 'errors.types')
      })

    this.$root.$on('updateLocale', () => {
      this.expectedDateOptions.locale = this.$i18n.locale
      this.deadlineDateOptions.locale = this.$i18n.locale
      this.editor.config.language = this.$i18n.locale
      this.editor.update += 1
    })

    this.$root.$on('resetNotification', (notification) => {
      this.resetNotification(notification)
    })
  },
  watch: {
    search: _.debounce(function () {
      let loader = this.$loading.show()
      let store = this.$store

      if (this.search) {
        if (this.form.type === 1) {
          kickstarterService.search(this.search)
            .then((results) => {
              this.searchResults = results
              loader.hide()
            }, () => {
              store.commit('error', 'errors.search')
              loader.hide()
            })
        } else if (this.form.type === 3) {
          gamefoundService.search(this.search)
            .then((results) => {
              this.searchResults = results
              loader.hide()
            }, () => {
              store.commit('error', 'errors.search')
              loader.hide()
            })
        }
      }
    }, 500),
    'editor.data': function () {
      this.form.description = this.editor.data
      this.$v.form.description.$touch()
    }
  },
  beforeDestroy: function () {
    this.$root.$off('updateLocale')
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .buttonNoPadding {
    padding: 0px 0px 1.5px;
    font-size: inherit;
  }
</style>



// WEBPACK FOOTER //
// src/pages/project/CreateProject.vue